import { Calendar, CalendarDays, Clock, Users, MoreHorizontal, Trash2 } from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  useGql,
} from '@ah/design';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DELETE_GATHERING_MUTATION, type GatheringRow, type GatheringUser } from '../shared/index.ts';

function userInitials(u: GatheringUser): string {
  if (u.firstName) return `${u.firstName[0]}${u.lastName?.[0] ?? ''}`.toUpperCase();
  return (u.name?.[0] ?? 'U').toUpperCase();
}

function userName(u: GatheringUser): string {
  if (u.firstName) return `${u.firstName} ${u.lastName ?? ''}`.trim();
  return u.name ?? 'Unknown';
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatDuration(startIso: string, endIso: string): string {
  const mins = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

const STATUS_LABELS: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  scheduled: { label: 'Scheduled', variant: 'secondary' },
  live: { label: 'Live', variant: 'default' },
  ended: { label: 'Ended', variant: 'outline' },
};

export function GatheringCard({ gathering, onClick }: { gathering: GatheringRow; onClick: () => void }) {
  const gql = useGql();
  const queryClient = useQueryClient();
  const { label, variant } = STATUS_LABELS[gathering.status] ?? { label: gathering.status, variant: 'outline' as const };
  const host = gathering.host;

  const deleteGathering = useMutation({
    mutationFn: () => gql.request(DELETE_GATHERING_MUTATION, { id: gathering.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gatherings'] });
      queryClient.invalidateQueries({ queryKey: ['gatherings-past'] });
    },
  });

  return (
    <Card className="cursor-pointer hover:bg-accent/40 transition-colors overflow-hidden" onClick={onClick}>
      <div className="flex">
        <div className="w-40 shrink-0 bg-muted/60 relative overflow-hidden">
          {gathering.coverUrl ? (
            <img src={gathering.coverUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/40">
              <CalendarDays size={40} strokeWidth={1.25} />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 p-4 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant={variant} className="shrink-0">
                  {gathering.status === 'live' && <span className="mr-1.5 w-1.5 h-1.5 rounded-full bg-green-400 inline-block animate-pulse" />}
                  {label}
                </Badge>
              </div>
              <p className="font-semibold text-foreground truncate">{gathering.title}</p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <button className="p-1 rounded hover:bg-accent transition-colors text-muted-foreground hover:text-foreground shrink-0 mt-0.5" aria-label="Gathering options">
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  disabled={deleteGathering.isPending}
                  onSelect={() => deleteGathering.mutate()}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deleteGathering.isPending ? 'Deleting…' : 'Delete gathering'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {gathering.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">{gathering.description}</p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {formatDateTime(gathering.startsAt)}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {formatDuration(gathering.startsAt, gathering.endsAt)}</span>
            <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {gathering.participantCount}</span>
          </div>
          {host && (
            <div className="flex items-center gap-2 pt-1">
              <Avatar className="w-5 h-5">
                <AvatarImage src={host.pictureUrl ?? ''} alt={userName(host)} />
                <AvatarFallback className="text-[10px]">{userInitials(host)}</AvatarFallback>
              </Avatar>
              <span className="text-xs text-muted-foreground">Hosted by {userName(host)}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
